import { useState } from 'react';
import { Outlet, Link as RouterLink, useNavigate } from 'react-router';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../hooks/useAuth';
import { usePageView } from '../hooks/usePageView';
import { useHashScroll } from '../hooks/useHashScroll';
import { useCookieConsent } from '../hooks/useCookieConsent';
import CookieConsentBanner from '../components/CookieConsentBanner';
import bindlesLogo from '../assets/bindles-primary.svg';
import { useActivePromotion } from '../hooks/usePromotions';
import { useDismissedPromotion } from '../hooks/useDismissedPromotion';
import PromotionBanner from '../components/PromotionBanner';
import trackEvent, { AnalyticsEvent } from '../utils/trackEvent';

export default function AppLayout() {
  const { logout } = useAuth();
  const { resetConsent } = useCookieConsent();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { data: activePromotion } = useActivePromotion();
  const { dismissedId, clearDismiss } = useDismissedPromotion();
  usePageView();
  useHashScroll();

  const handleLogout = () => {
    logout();
    void navigate('/');
  };

  const toggleDrawer = () => {
    setMobileOpen((prev) => !prev);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <PromotionBanner />
      <AppBar
        position="sticky"
        color="transparent"
        elevation={0}
        sx={{ bgcolor: 'background.default' }}
      >
        <Toolbar>
          <Box component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <Box component="img" src={bindlesLogo} alt="bindles" sx={{ height: 36 }} />
          </Box>

          {/* Desktop nav */}
          <Stack
            direction="row"
            spacing={2}
            sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}
          >
            <Button component={RouterLink} to="/app/dashboard" color="inherit">
              Dashboard
            </Button>
            <Button component={RouterLink} to="/app/licenses" color="inherit">
              Licenses
            </Button>
            <Button component={RouterLink} to="/app/change-password" color="inherit">
              Change Password
            </Button>
            <Button component={RouterLink} to="/app/update-email" color="inherit">
              Update Email
            </Button>
            <Button component={RouterLink} to="/app/email-preferences" color="inherit">
              Email Preferences
            </Button>
          </Stack>
          <Button
            onClick={handleLogout}
            color="inherit"
            sx={{ display: { xs: 'none', md: 'inline-flex' } }}
          >
            Sign Out
          </Button>

          {/* Mobile hamburger */}
          <Box sx={{ flexGrow: 1, display: { xs: 'block', md: 'none' } }} />
          <IconButton
            color="inherit"
            aria-label="open navigation menu"
            edge="end"
            onClick={toggleDrawer}
            sx={{ display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer anchor="right" open={mobileOpen} onClose={toggleDrawer}>
        <Box sx={{ width: 250 }} role="presentation" onClick={toggleDrawer}>
          <List>
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/app/dashboard">
                <ListItemText primary="Dashboard" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/app/licenses">
                <ListItemText primary="Licenses" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/app/change-password">
                <ListItemText primary="Change Password" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/app/update-email">
                <ListItemText primary="Update Email" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/app/email-preferences">
                <ListItemText primary="Email Preferences" />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemText primary="Sign Out" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, py: 4 }}>
        <Outlet />
      </Box>

      <Box component="footer" sx={{ py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Container>
          <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap">
            <Link
              component="button"
              variant="body2"
              color="text.secondary"
              onClick={resetConsent}
              sx={{ cursor: 'pointer' }}
            >
              Cookie Settings
            </Link>
            {activePromotion && dismissedId === activePromotion.id && (
              <Link
                component="button"
                variant="body2"
                color="text.secondary"
                onClick={() => {
                  clearDismiss();
                  trackEvent(AnalyticsEvent.PROMOTION_FOOTER_REOPEN, {
                    promotion_id: activePromotion.id,
                    code: activePromotion.code,
                  });
                }}
                sx={{ cursor: 'pointer' }}
              >
                View Current Promotions
              </Link>
            )}
          </Stack>
        </Container>
      </Box>

      <CookieConsentBanner />
    </Box>
  );
}
